import React, { useCallback, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import type { SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import type { DraftWorkItem } from '@atlassian/jira-create-work-items/useAiCreateProjectContext/types.tsx';
import { JiraIssueTypeIcon } from '@atlassian/jira-platform-fields/JiraIssueTypeIcon.tsx';

import { ProjectSelector } from '../../../../../common/ui/project-selector';

import type {
	previewContent_AtlassianConversationAssistantPreviewCard$data as ProjectData,
	previewContent_AtlassianConversationAssistantPreviewCard$key as ProjectKey,
} from './__generated__/previewContent_AtlassianConversationAssistantPreviewCard.graphql';
import { Field } from './field';
import { WithFieldLabel } from './with-field-label';

type IssueTypeEdge = NonNullable<NonNullable<ProjectData['issueTypes']>['edges']>[number];
type IssueTypeNode = NonNullable<IssueTypeEdge>['node'];
type FieldConfigurationEdges = NonNullable<
	NonNullable<IssueTypeNode>['fieldsConfiguration']
>['edges'];
type FieldConfigurationEdge = NonNullable<FieldConfigurationEdges>[number];
type FieldNode = Exclude<NonNullable<FieldConfigurationEdge>['node'], undefined>;

const SUMMARY_FIELD_NAME = 'summary';
const DESCRIPTION_FIELD_NAME = 'description';
const ISSUE_TYPE_FIELD_NAME = 'issuetype';

const FIELDS_NOT_RENDERED = new Set([
	SUMMARY_FIELD_NAME,
	DESCRIPTION_FIELD_NAME,
	ISSUE_TYPE_FIELD_NAME,
]);

const isDefined = (value: unknown) => value !== null && value !== undefined;

const messages = defineMessages({
	descriptionHeading: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.description-field',
		description:
			'The label for the description field in the single work item preview card for the Jira AIFC experience',
		defaultMessage: 'Description',
	},
	detailsHeading: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.details-heading',
		description:
			'The heading for the details section in the single work item preview card for the Jira AIFC experience',
		defaultMessage: 'Details',
	},
});

const styles = cssMap({
	issueTypeIconWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '1.5rem',
		width: '1.5rem',
	},
});

type FieldsToRender = {
	issueType: IssueTypeNode;
	summaryField: {
		workItemFieldId: string;
		fieldValue: string;
		fieldNode: FieldNode;
	};
	descriptionField: {
		workItemFieldId: string;
		fieldValue: unknown;
		fieldNode: FieldNode;
	};
	detailsFields: {
		workItemFieldId: string;
		fieldValue: unknown;
		fieldNode: FieldNode;
	}[];
};

const getFieldsToRender = (
	projectData: ProjectData,
	workItem: DraftWorkItem,
): FieldsToRender | null => {
	const issueType = projectData?.issueTypes?.edges?.find(
		(edge) => edge?.node?.issueTypeId === workItem.issueTypeId,
	)?.node;

	if (!issueType) {
		return null;
	}

	// first find the correct issuetype based on the issueTypeId, then create a map of fieldId to {fieldNode, isRequired}
	const allFieldsMap = new Map<string, FieldNode>();
	issueType.fieldsConfiguration?.edges?.forEach((fieldEdge) => {
		const node = fieldEdge?.node;
		if (!node) {
			return;
		}

		allFieldsMap.set(node.fieldId, fieldEdge?.node);
	}) ?? [];

	const detailsFields = Object.entries(workItem?.fields ?? {})
		.map(([workItemFieldId, fieldValue]) => {
			return {
				workItemFieldId,
				fieldValue,
				fieldNode: allFieldsMap.get(workItemFieldId) ?? null,
			};
		})
		// We don't render the summary, description or issuetype in the details section
		.filter((field) => !FIELDS_NOT_RENDERED.has(field.workItemFieldId));

	const summaryField = {
		workItemFieldId: SUMMARY_FIELD_NAME,
		fieldValue: workItem?.summary,
		fieldNode: allFieldsMap.get(SUMMARY_FIELD_NAME) ?? null,
	};
	const descriptionField = {
		workItemFieldId: DESCRIPTION_FIELD_NAME,
		fieldValue: workItem?.fields?.description,
		fieldNode: allFieldsMap.get(DESCRIPTION_FIELD_NAME) ?? null,
	};

	return {
		issueType,
		summaryField,
		descriptionField,
		detailsFields,
	};
};

const SectionHeading = ({ heading }: { heading: string }) => {
	return (
		<Text size="medium" weight="medium">
			{heading}
		</Text>
	);
};

/**
 * The content that is displayed in the chat preview card.
 */
export const PreviewContent = ({
	cloudId,
	isEditable,
	isCreated,
	project,
	workItem,
	projectKey,
	onFieldChange,
	isStagingArea,
	onProjectChange,
	sendAifcAnalyticsEvent,
}: {
	cloudId: string;
	isEditable: boolean;
	isCreated: boolean;
	project: ProjectKey;
	workItem: DraftWorkItem;
	projectKey: string;
	onFieldChange: (
		invocationId: string,
		fieldId: string,
		newValue: unknown,
		skipFireAnalytics?: boolean,
	) => void;
	isStagingArea: boolean;
	onProjectChange: (projectKey: string) => void;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const projectData = useFragment<ProjectKey>(
		graphql`
			fragment previewContent_AtlassianConversationAssistantPreviewCard on JiraProject {
				id
				name
				avatar {
					xsmall
				}
				issueTypes {
					edges {
						node {
							issueTypeId
							...JiraIssueTypeIcon_Platform
							fieldsConfiguration {
								edges {
									node {
										__typename
										fieldId
										...field_AtlassianConversationAssistantPreviewCard
										...withFieldLabel_AtlassianConversationAssistantPreviewCard
									}
								}
							}
						}
					}
				}
			}
		`,
		project,
	);

	// Project info of current project to initially be displayed in the project selector
	const projectId = projectData.id;
	const projectName = projectData.name;
	const projectIcon = projectData.avatar?.xsmall ?? undefined;

	const invocationId = workItem.invocationId;
	const fieldsToRender = useMemo(
		() => getFieldsToRender(projectData, workItem),
		[projectData, workItem],
	);

	const setProject = useCallback(
		({ projectKey: pk }: { projectKey: string }) => onProjectChange(pk),
		[onProjectChange],
	);

	if (!fieldsToRender || !fieldsToRender.issueType) {
		return null;
	}

	return (
		<Stack space="space.100">
			<Stack>
				{FeatureGates.getExperimentValue(
					'jira-aifc-project-selector-in-chat-exp',
					'isEnabled',
					false,
				) ? (
					<Inline space="space.050" alignBlock="center">
						<ProjectSelector
							isIconOnly
							isDisabled={!isEditable || isCreated}
							siteId={cloudId}
							selectedProject={{ label: projectName, value: projectId, projectKey }}
							selectedProjectIconUrl={projectIcon}
							setProject={setProject}
						/>
						<Text size="medium" weight="medium" color="color.text.subtlest">
							/
						</Text>
						<Box xcss={styles.issueTypeIconWrapper}>
							<JiraIssueTypeIcon issueType={fieldsToRender.issueType} showTooltip={true} />
						</Box>
					</Inline>
				) : (
					<Inline space="space.100" alignBlock="center">
						<JiraIssueTypeIcon issueType={fieldsToRender.issueType} showTooltip={true} />
					</Inline>
				)}
				<Field
					field={fieldsToRender.summaryField.fieldNode}
					fieldValue={fieldsToRender.summaryField.fieldValue}
					onFieldChange={onFieldChange}
					projectKey={projectKey}
					invocationId={invocationId}
					isEditable={isEditable}
					as="heading"
					isFullWidth
					isStagingArea={isStagingArea}
					sendAifcAnalyticsEvent={sendAifcAnalyticsEvent}
				/>
			</Stack>

			{isDefined(fieldsToRender.descriptionField.fieldValue) && (
				<Stack>
					<SectionHeading heading={formatMessage(messages.descriptionHeading)} />
					<Field
						field={fieldsToRender.descriptionField.fieldNode}
						fieldValue={fieldsToRender.descriptionField.fieldValue}
						onFieldChange={onFieldChange}
						projectKey={projectKey}
						invocationId={invocationId}
						isFullWidth
						isEditable={isEditable}
						isStagingArea={isStagingArea}
						sendAifcAnalyticsEvent={sendAifcAnalyticsEvent}
					/>
				</Stack>
			)}
			{fieldsToRender.detailsFields.length > 0 && (
				<Stack space="space.100">
					<SectionHeading heading={formatMessage(messages.detailsHeading)} />
					<Stack space="space.050">
						{fieldsToRender.detailsFields.map(({ workItemFieldId, fieldValue, fieldNode }) => (
							// Fallback to the fieldId if the fieldName is not found, specifically this handles the parent field
							// which may not exist in the response from the API
							<WithFieldLabel
								key={workItemFieldId}
								fieldConfig={fieldNode}
								fallbackFieldName={workItemFieldId}
							>
								<Field
									field={fieldNode}
									fieldValue={fieldValue}
									onFieldChange={onFieldChange}
									projectKey={projectKey}
									invocationId={invocationId}
									isEditable={isEditable}
									isStagingArea={isStagingArea}
									sendAifcAnalyticsEvent={sendAifcAnalyticsEvent}
								/>
							</WithFieldLabel>
						))}
					</Stack>
				</Stack>
			)}
		</Stack>
	);
};
